import Store from '../../store';
import { ROLES, ABNORMALITIES, RECOVERY_ACTIONS } from '../../constants';
import map from 'lodash/map';
import some from 'lodash/some';

/*
 * Checks whether a user has the required access permissions to perform a specific action.
 */
export function canViewAbnormalityDetails(abnormalityType) {
    switch (abnormalityType) {
        case ABNORMALITIES.BLOWOUT:
            return canViewBlowOut();
        case ABNORMALITIES.DELAY:
            return canViewDelay();
        case ABNORMALITIES.DAMAGE:
        case ABNORMALITIES.EARLY_FREIGHT:
        case ABNORMALITIES.GENERIC:
        case ABNORMALITIES.INTERNAL_LOGISTICS:
        case ABNORMALITIES.LATE_FREIGHT:
        case ABNORMALITIES.NEW_ORDER:
        case ABNORMALITIES.RETURN_FREIGHT:
        case ABNORMALITIES.SHORT_SHIPMENT:
        case ABNORMALITIES.PARTIAL_SHORT_SHIPMENT:
        case ABNORMALITIES.UNABLE_TO_LOCATE:
            return canViewGeneric();
        case ABNORMALITIES.MISCOLLECTION:
            return canViewMiscollection();
        default:
            return false;
    }
}

export function canEditAbnormality(abnormalityType) {
    switch (abnormalityType) {
        case ABNORMALITIES.BLOWOUT:
            return canEditBlowOut();
        case ABNORMALITIES.DELAY:
            return canEditDelay();
        case ABNORMALITIES.DAMAGE:
        case ABNORMALITIES.EARLY_FREIGHT:
        case ABNORMALITIES.GENERIC:
        case ABNORMALITIES.INTERNAL_LOGISTICS:
        case ABNORMALITIES.LATE_FREIGHT:
        case ABNORMALITIES.NEW_ORDER:
        case ABNORMALITIES.RETURN_FREIGHT:
        case ABNORMALITIES.SHORT_SHIPMENT:
        case ABNORMALITIES.PARTIAL_SHORT_SHIPMENT:
        case ABNORMALITIES.UNABLE_TO_LOCATE:
            return canEditGeneric();
        case ABNORMALITIES.MISCOLLECTION:
            return canEditMiscollection();
        default:
            return false;
    }
}

export function canViewRecoveryActionDetails(recoveryActionType) {
    switch (recoveryActionType) {
        case RECOVERY_ACTIONS.INSERT_MANIFEST:
            return canViewInsert();
        case RECOVERY_ACTIONS.PULL_FORWARD:
            return canViewPullForward();
        case RECOVERY_ACTIONS.SLIDE:
            return canViewSlide();
        case RECOVERY_ACTIONS.SPECIAL:
            return canViewSpecial();
        case RECOVERY_ACTIONS.TAP:
            return canViewTap();
        default:
            return false;
    }
}

export function canEditRecoveryAction(recoveryActionType) {
    switch (recoveryActionType) {
        case RECOVERY_ACTIONS.INSERT_MANIFEST:
            return canEditInsert();
        case RECOVERY_ACTIONS.PULL_FORWARD:
            return canEditPullForward();
        case RECOVERY_ACTIONS.SLIDE:
            return canEditSlide();
        case RECOVERY_ACTIONS.SPECIAL:
            return canEditSpecial();
        case RECOVERY_ACTIONS.TAP:
            return canEditTap();
        default:
            return false;
    }
}

/*
 * DASHBOARD TABS
 */
// Checks if current user has permission to view the abnormality tabs on the dashboard.
export function canViewAbnormalityDashboard() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.DASHBOARD_ABNORMALITY_VIEWER);
    // console.log('[isActionAuthorized] User is allowed to view abnormality dashboard?', authorized);
    return authorized;
}


export function canViewIlocsDownload() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.REPORTS_ILOCS_DATA_DOWNLOADER);
    return authorized;
}

// Checks if current user has permission to view the delay tab on the dashboard.
export function canViewDelayDashboard() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.DASHBOARD_DELAY_VIEWER);
    // console.log('[isActionAuthorized] User is allowed to view delay dashboard?', authorized);
    return authorized;
}

// Checks if current user has permission to view the recovery actions tab on the dashboard.
export function canViewRecoveryActionDashboard() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.DASHBOARD_RECOVERY_ACTION_VIEWER);
    // console.log('[isActionAuthorized] User is allowed to view recovery action dashboard?', authorized);
    return authorized;
}

// Checks if current user has permission to view the recovery actions tab on the dashboard.
export function canViewTrackingDashboard() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.DASHBOARD_TRACKING_VIEWER);
    // console.log('[isActionAuthorized] User is allowed to view tracking dashboard?', authorized);
    return authorized;
}

/*
 * RUNS
 */
// Checks if current user has permission to view a run details screen, in read-only mode.
export function canViewRunDetails() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RUN_DETAILS_VIEWER);
    // console.log('[isActionAuthorized] User is allowed to view run details?', authorized);
    return authorized;
}

// Checks if current user has permission to add a manual GPS location to a run, update its comment and to create an abnormality.
export function canEditRunDetailsAsLP() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RUN_DETAILS_LP_EDITOR);
    // console.log('[isActionAuthorized] User is allowed to edit run details as LP?', authorized);
    return authorized;
}

// Checks if current user has permission to add a manual GPS location to a run, update its comment, add locations,
// disable live tracking, view run manifests, and create an abnormality.
export function canEditRunDetailsAsTME() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RUN_DETAILS_TME_EDITOR);
    // console.log('[isActionAuthorized] User is allowed to edit run details as TME?', authorized);
    return authorized;
}

/*
 * MANIFESTS
 */
// Checks if current user has permission to view details of a manifest
export function canViewManifestDetails() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.MANIFEST_DETAIL_VIEWER);
    // console.log('[isActionAuthorized] User is allowed to view a manifest\'s details?', authorized);
    return authorized;
}

/*
 * ABNORMALITIES
 */
// Checks if current user has permission to view a blow out abnormality, in read-only mode.
export function canViewBlowOut() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_BLOWOUT_VIEWER);
    // console.log('[isActionAuthorized] User is allowed to view a blow out abnormality?', authorized);
    return authorized;
}

// Checks if current user has permission to view a miscollection abnormality, in read-only mode.
export function canViewMiscollection() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_MISCOLLECTION_VIEWER);
    // console.log('[isActionAuthorized] User is allowed to view a miscollection abnormality?', authorized);
    return authorized;
}

// Checks if current user has permission to view a generic abnormality, in read-only mode.
export function canViewGeneric() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_GENERIC_VIEWER);
    // console.log('[isActionAuthorized] User is allowed to view a generic abnormality?', authorized);
    return authorized;
}

// Checks if current user has permission to view a delay abnormality, in read-only mode.
export function canViewDelay() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_DELAY_VIEWER);
    // console.log('[isActionAuthorized] User is allowed to view a delay abnormality?', authorized);
    return authorized;
}

// Checks if current user has permission to create and update a blow out.
export function canCreateBlowOut() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_BLOWOUT_CREATOR);
    // console.log('[isActionAuthorized] User is allowed to create a blow out abnormality?', authorized);
    return authorized;
}

// Checks if current user has permission to create and update a miscollection.
export function canCreateMiscollection() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_MISCOLLECTION_CREATOR);
    // console.log('[isActionAuthorized] User is allowed to create a miscollection abnormality?', authorized);
    return authorized;
}

// Checks if current user has permission to create and update a generic abnormality.
export function canCreateGeneric() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_GENERIC_CREATOR);
    // console.log('[isActionAuthorized] User is allowed to create a generic abnormality?', authorized);
    return authorized;
}

// Checks if current user has permission to create and update a delay.
export function canCreateDelay() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_DELAY_CREATOR);
    // console.log('[isActionAuthorized] User is allowed to create a delay abnormality?', authorized);
    return authorized;
}

// Checks if current user has permission to mark a blow out as handled.
export function canEditBlowOut() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_BLOWOUT_EDITOR);
    // console.log('[isActionAuthorized] User is allowed to edit a blow out abnormality?', authorized);
    return authorized;
}

// Checks if current user has permission to mark a miscollection as handled.
export function canEditMiscollection() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_MISCOLLECTION_EDITOR);
    // console.log('[isActionAuthorized] User is allowed to edit a miscollection abnormality?', authorized);
    return authorized;
}

// Checks if current user has permission to mark a generic abnormality as handled.
export function canEditGeneric() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_GENERIC_EDITOR);
    // console.log('[isActionAuthorized] User is allowed to edit a generic abnormality?', authorized);
    return authorized;
}

// Checks if current user has permission to mark a delay as handled.
export function canEditDelay() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_DELAY_EDITOR);
    // console.log('[isActionAuthorized] User is allowed to edit a delay abnormality?', authorized);
    return authorized;
}

// Checks if current user has permission to cancel a blow out.
export function canCancelBlowOut() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_BLOWOUT_CANCELLER);
    // console.log('[isActionAuthorized] User is allowed to cancel a blow out abnormality?', authorized);
    return authorized;
}

// Checks if current user has permission to cancel a delay.
export function canCancelDelay() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_DELAY_CANCELLER);
    // console.log('[isActionAuthorized] User is allowed to cancel a delay abnormality?', authorized);
    return authorized;
}

// Checks if current user has permission to cancel a generic abnormality.
export function canCancelGeneric() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_GENERIC_CANCELLER);
    // console.log('[isActionAuthorized] User is allowed to cancel a generic abnormality?', authorized);
    return authorized;
}

// Checks if current user has permission to cancel a miscollection.
export function canCancelMiscollection() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_MISCOLLECTION_CANCELLER);
    // console.log('[isActionAuthorized] User is allowed to cancel a miscollection abnormality?', authorized);
    return authorized;
}

// Checks if current user has permission to close a blow out.
export function canCloseBlowOut() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_BLOWOUT_CLOSER);
    // console.log('[isActionAuthorized] User is allowed to close a blow out abnormality?', authorized);
    return authorized;
}

// Checks if current user has permission to close a delay.
export function canCloseDelay() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_DELAY_CLOSER);
    // console.log('[isActionAuthorized] User is allowed to close a delay abnormality?', authorized);
    return authorized;
}

// Checks if current user has permission to close a generic abnormality.
export function canCloseGeneric() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_GENERIC_CLOSER);
    // console.log('[isActionAuthorized] User is allowed to close a generic abnormality?', authorized);
    return authorized;
}

// Checks if current user has permission to close miscollection.
export function canCloseMiscollection() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.ABNORMALITY_MISCOLLECTION_CLOSER);
    // console.log('[isActionAuthorized] User is allowed to close a miscollection abnormality?', authorized);
    return authorized;
}

/*
 * RECOVERY ACTIONS
 */
// Checks if current user has permission to view an insert, in read-only mode.
export function canViewInsert() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_INSERT_MANIFEST_VIEWER);
    // console.log('[isActionAuthorized] User is allowed to view an insert recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to view a pull forward, in read-only mode.
export function canViewPullForward() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_PULL_FORWARD_VIEWER);
    // console.log('[isActionAuthorized] User is allowed to view a pull forward recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to view a slide, in read-only mode.
export function canViewSlide() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_SLIDE_VIEWER);
    // console.log('[isActionAuthorized] User is allowed to view a slide recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to view a special, in read-only mode.
export function canViewSpecial() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_SPECIAL_VIEWER);
    // console.log('[isActionAuthorized] User is allowed to view a special recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to view a tap, in read-only mode.
export function canViewTap() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_TAP_VIEWER);
    // console.log('[isActionAuthorized] User is allowed to view a tap recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to view an EMA, in read-only mode.
export function canViewEMA() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_EMA_VIEWER);
    // console.log('[isActionAuthorized] User is allowed to view an EMA recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to view an SMA, in read-only mode.
export function canViewSMA() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_SMA_VIEWER);
    // console.log('[isActionAuthorized] User is allowed to view an SMA recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to mark an insert as executed.
export function canExecuteInsert() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_INSERT_MANIFEST_EXECUTOR);
    // console.log('[isActionAuthorized] User is allowed to execute an insert recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to mark a pull forward as executed.
export function canExecutePullForward() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_PULL_FORWARD_EXECUTOR);
    // console.log('[isActionAuthorized] User is allowed to execute a pull forward recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to mark a slide as executed.
export function canExecuteSlide() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_SLIDE_EXECUTOR);
    // console.log('[isActionAuthorized] User is allowed to execute a slide recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to mark a special as executed.
export function canExecuteSpecial() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_SPECIAL_EXECUTOR);
    // console.log('[isActionAuthorized] User is allowed to execute a special recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to mark a tap as executed.
export function canExecuteTap() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_TAP_EXECUTOR);
    // console.log('[isActionAuthorized] User is allowed to execute a tap recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to mark an EMA as executed.
export function canExecuteEMA() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_EMA_EXECUTOR);
    // console.log('[isActionAuthorized] User is allowed to execute an EMA recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to mark an SMA as executed.
export function canExecuteSMA() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_SMA_EXECUTOR);
    // console.log('[isActionAuthorized] User is allowed to execute an SMA recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to create and update an insert.
export function canEditInsert() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_INSERT_MANIFEST_EDITOR);
    // console.log('[isActionAuthorized] User is allowed to edit an insert recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to create and update a pull forward.
export function canEditPullForward() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_PULL_FORWARD_EDITOR);
    // console.log('[isActionAuthorized] User is allowed to edit a pull forward recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to create and update a slide.
export function canEditSlide() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_SLIDE_EDITOR);
    // console.log('[isActionAuthorized] User is allowed to edit a slide recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to create and update a special.
export function canEditSpecial() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_SPECIAL_EDITOR);
    // console.log('[isActionAuthorized] User is allowed to edit a special recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to create and update a tap.
export function canEditTap() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_TAP_EDITOR);
    // console.log('[isActionAuthorized] User is allowed to edit a tap recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to create and update an EMA.
export function canEditEMA() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_EMA_EDITOR);
    // console.log('[isActionAuthorized] User is allowed to edit an EMA recovery action?', authorized);
    return authorized;
}

// Checks if current user has permission to create and update an SMA.
export function canEditSMA() {
    const roles = Store.store.getState().session.user.functions;
    const authorized = some(roles, r => r === ROLES.RECOVERY_ACTION_SMA_EDITOR);
    // console.log('[isActionAuthorized] User is allowed to edit an SMA recovery action?', authorized);
    return authorized;
}

/**
 * Specific checks
 */
export function canEditFieldAsGroupAssignee(requiredGroupRoles) {
    const groupRoles = Store.store.getState().session.user.groupRoles;
    let authorized = false;
    if (groupRoles.length) {
        const requiredGroupRoleNames = map(requiredGroupRoles, groupRole => groupRole.name);
        authorized = some(groupRoles, r => requiredGroupRoleNames.indexOf(r) > -1);
    } else {
        // No group roles => admin? TODO: improve this logic...
        authorized = true;
    }
    console.log(
        '[isActionAuthorized] User is allowed to edit a field as a certain group role?',
        authorized
    );
    return authorized;
}
